import VoiceOpenQuestion from './voice/VoiceOpenQuestion';
import VoiceHidden from './voice/VoiceHidden';
import VoiceSerial from './voice/VoiceSerial';
import Memory from './memory/Memory';
import MemoryCascade from './memory/MemoryCascade';
import Reading from './speechTasks/Reading';
import Repetition from './speechTasks/Repetition';
import Naming from './speechTasks/Naming';
import Drawing from './drawing/Drawing';
import TrailMaking from './drawing/TrailMaking'; 
import TextInput from './speechTasks/TextInput';
import ManualAction from './speechTasks/ManualAction';
import ReadingAction from './speechTasks/ReadingAction';
import Coding from './coding/Coding';
import CodingV2 from './coding/CodingV2';
import Stroop from './stroop/Stroop';
import ArticulationGame from '../../exercises-engine/gameTypes/articulation/ArticulationGame'; // НОВЫЙ ИМПОРТ

const extractNumbers = (text) => {
  const map = { 'девяносто три': 93, 'восемьдесят шесть': 86, 'семьдесят девять': 79, 'семьдесят два': 72, 'шестьдесят пять': 65 };
  let numbers = [];
  const digits = text.match(/\d+/g);
  if (digits) numbers = digits.map(Number);
  Object.keys(map).forEach(k => { if (text.includes(k)) numbers.push(map[k]); });
  return numbers;
};

const wordToDigit = (word) => {
  const map = {
    'один': '1', '1': '1', 'два': '2', '2': '2', 'три': '3', '3': '3',
    'четыре': '4', '4': '4', 'пять': '5', '5': '5', 'шесть': '6', '6': '6',
    'семь': '7', '7': '7', 'восемь': '8', '8': '8', 'девять': '9', '9': '9'
  };
  const cleanWord = word.replace(/[.,!?]/g, '');
  return map[cleanWord] || null;
};

// Задание 5 (запоминание слов, балл не ставится). После последней попытки —
// 1-й (если вспомнил все слова сразу) или 2-й — говорим пациенту фразу
// «Я попрошу повторить эти слова в конце теста» и только потом идём дальше.
// Текст можно поменять в JSON шкалы (поле closing / closing_audio_url);
// если поля нет — берётся текст по умолчанию.
const MEMORY_CLOSING_DEFAULT = 'Я попрошу повторить эти слова в конце теста';

const finishMemoryTrial = (question, ctx, found, hasReadTwice) => {
  // closing: true защищает от повторного вызова, пока звучит фраза
  ctx.setTaskState({ found, hasReadTwice, closing: true });
  // Останавливаем микрофон ДО фразы, чтобы он не записал голос самой программы.
  ctx.stopAll();
  const goNext = () => ctx.next(`Вспомнил: ${found.length} из 5`, found.length);
  if (question.closing_audio_url) {
    ctx.playAudio(question.closing_audio_url, goNext);
  } else {
    ctx.speak(question.closing || MEMORY_CLOSING_DEFAULT, goNext);
  }
};

export const itemTypeRegistry = {
  // НОВЫЙ ТИП ДЛЯ CV В ШКАЛАХ
  cv_face_action: {
    View: ArticulationGame,
    capturesSpeech: false, // Флаг от Клода: не включать микрофон!
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  
  // 1а NIHSS — уровень бодрствования с эскалацией стимуляции.
  // Голосом: 3 попытки (с 1-й = 0 баллов, со 2-й = 1, с 3-й = 2, нет
  // реакции = 3). Молчание = 7 секунд. «Показать»: одна попытка.
  consciousness: {
    View: VoiceOpenQuestion,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let current = state.answer || '';
      const cleanFinal = (finalText && finalText !== 'undefined') ? finalText : '';
      if (cleanFinal && !current.includes(cleanFinal)) current = (current + ' ' + cleanFinal).trim();
      ctx.setTaskState({ answer: current, interim: interimText });
    },
    onSilenceTimeout: (question, ctx, mode) => {
      const state = ctx.getTaskState() || {};
      const attempt = state.attempt || 1;
      const answer = (state.answer || '').trim();

      if (mode === 'show') {
        // «Показать», одна попытка: сюда попадаем, только если не кликнул
        // и не сказал ни слова — ответа нет, оценка врачу.
        ctx.next(JSON.stringify({ outcome: 'no_response', attempt: 1 }), 0);
        return;
      }

      if (answer) {
        const points = Math.min(attempt - 1, 2);
        ctx.next(JSON.stringify({ outcome: 'answered', attempt, answer, points }), points);
        return;
      }
      if (attempt === 1) {
        ctx.setTaskState({ attempt: 2, answer: '' });
        ctx.speak(question.ask2 || question.ask, () => ctx.startListening());
        return;
      }
      if (attempt === 2) {
        ctx.setTaskState({ attempt: 3, answer: '' });
        ctx.speak(question.ask3 || 'Вы меня слышите?', () => ctx.startListening());
        return;
      }
      ctx.next(JSON.stringify({ outcome: 'no_response', attempt: 3, points: 3 }), 3);
    }
  },

  stroop: {
    View: Stroop,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  coding: {
    View: Coding,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const fullText = (finalText + ' ' + interimText).toLowerCase().trim();
      if (!fullText) return;

      const state = ctx.getTaskState() || {};
      const prevDigits = state.digits || [];

      const words = fullText.split(/\s+/).filter(Boolean);
      const newDigits = [];

      for (const word of words) {
        const d = wordToDigit(word);
        if (d) newDigits.push(d);
      }

      if (newDigits.length > prevDigits.length) {
        ctx.setTaskState({ ...state, digits: newDigits });
      }
    },
    onSilenceTimeout: (question, ctx) => {}
  },
  coding_v2: {
    View: CodingV2,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const fullText = (finalText + ' ' + interimText).toLowerCase().trim();
      if (!fullText) return;

      const state = ctx.getTaskState() || {};
      const prevDigits = state.digits || [];

      const words = fullText.split(/\s+/).filter(Boolean);
      const newDigits = [];

      for (const word of words) {
        const d = wordToDigit(word);
        if (d) newDigits.push(d);
      }

      if (newDigits.length > prevDigits.length) {
        ctx.setTaskState({ ...state, digits: newDigits });
      }
    },
    onSilenceTimeout: (question, ctx) => {}
  },

  text_input: {
    View: TextInput,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  manual_action: {
    View: ManualAction,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  reading_action: {
    View: ReadingAction,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  trail_making: {
    View: TrailMaking, 
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
  drawing: {
    View: Drawing,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
    voice: {
    View: VoiceOpenQuestion,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let current = state.answer || '';
      
      const cleanFinal = (finalText && finalText !== 'undefined') ? finalText : '';
      const cleanInterim = (interimText && interimText !== 'undefined') ? interimText : '';

      if (cleanFinal && !current.includes(cleanFinal)) {
        current = (current + ' ' + cleanFinal).trim();
      }
      ctx.setTaskState({ answer: current, interim: cleanInterim });
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      let answer = (state.answer || '') + ' ' + (state.interim || '');
      answer = answer.trim();
      
      if (question.isTrial) {
        const isCorrect = question.trialCorrect.some(w => answer.toLowerCase().includes(w.toLowerCase()));
        if (isCorrect) {
          ctx.next(answer, 0);
        } else {
          // НОВОЕ: Проигрываем аудио правильного ответа, если есть ссылка
          if (question.trialAnswer_audio_url) {
            ctx.playAudio(question.trialAnswer_audio_url, () => ctx.next(answer, 0));
          } else {
            ctx.speak(question.trialAnswer, () => ctx.next(answer, 0));
          }
        }
        return;
      }
      ctx.next(answer || 'Ответ не распознан', question.timeoutPoints || 0);
    }
  },
  vigilance: {
    View: VoiceOpenQuestion,
    onRecognitionResult: () => {},
    onSilenceTimeout: () => {}
  },
    voice_hidden: {
    View: VoiceHidden,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let words = state.words || new Set(); // НОВОЕ: Используем Set для удаления дублей
      
      if (finalText) {
        const finalWords = finalText.toLowerCase().replace(/[^а-яё\s]/g, '').split(/\s+/).filter(Boolean);
        finalWords.forEach(w => words.add(w));
      }
      
      const current = [...words].join(' '); // Собираем строку из уникальных слов
      ctx.setTaskState({ words, accumulated: current, interim: interimText });
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      const answer = (state.accumulated + ' ' + (state.interim || '')).trim();
      ctx.next(answer || 'Ответ не распознан');
    }
  },
  
  voice_serial: {
    View: VoiceSerial,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let history = state.history || [];
      let base = state.base || 100;
      let score = state.score || 0;
      let processedText = state.processedText || '';
      const maxSerial = question.maxScore || 3;
      
      if (finalText && finalText !== processedText) {
        const newText = processedText ? finalText.replace(processedText, '') : finalText;
        const nums = extractNumbers(newText.toLowerCase());
        
        for (const lastNum of nums) {
          const expected = base - 7;
          if (lastNum === expected) score++;
          base = lastNum;
          history = [...history, lastNum];
          processedText = finalText; 
          
          if (history.length >= 5) {
            const historyStr = history.join(', ');
            ctx.next(`${historyStr} (Балл: ${Math.min(score, maxSerial)} из ${maxSerial})`, Math.min(score, maxSerial));
            return; 
          }
        }
        ctx.setTaskState({ history, base, score, processedText });
      }
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      let score = state.score || 0;
      let history = state.history || [];
      const maxSerial = question.maxScore || 3;
      
      const historyStr = history.length > 0 ? history.join(', ') : 'Нет ответа';
      ctx.next(`${historyStr} (Балл: ${Math.min(score, maxSerial)} из ${maxSerial})`, Math.min(score, maxSerial));
    }
  },  
  memory: {
    View: Memory,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const fullText = (finalText + ' ' + interimText).toLowerCase();
      const state = ctx.getTaskState() || {};
      if (state.closing) return;
      let found = state.found || [];
      let hasReadTwice = state.hasReadTwice || false;
      
      question.words.forEach(w => { if (fullText.includes(w) && !found.includes(w)) found.push(w); });
      ctx.setTaskState({ found, hasReadTwice });
      
       if (found.length === question.words.length) {
        finishMemoryTrial(question, ctx, found, hasReadTwice);
      }
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      if (state.closing) return;
      let found = state.found || [];
      let hasReadTwice = state.hasReadTwice || false;
      
            if (!hasReadTwice) {
        ctx.setTaskState({ found: [], hasReadTwice: true });
        if (question.ask2_audio_url) {
          ctx.playAudio(question.ask2_audio_url, () => ctx.startListening());
        } else {
          ctx.speak(question.ask2, () => ctx.startListening());
        }
       } else {
        finishMemoryTrial(question, ctx, found, hasReadTwice);
      }
    }
  },
    memory_cascade: {
    View: MemoryCascade,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
       const fullText = (finalText + ' ' + interimText).toLowerCase();
       const state = ctx.getTaskState() || {};
       let found = state.found || []; // Теперь это массив объектов { word, status }
       let missed = state.missed || [];
       let phase = state.phase || 'free';
       let missIdx = state.missIdx || 0;

       // НОВАЯ ФУНКЦИЯ: проверка, вспомнено ли слово
       const isFound = (w) => found.some(f => f.word === w);

       if (phase === 'hint') {
         const targetWord = missed[missIdx];
         if (targetWord && fullText.includes(targetWord) && !isFound(targetWord)) {
           found.push({ word: targetWord, status: 'hint' });
           
           if (missIdx < missed.length - 1) {
            missIdx++;
             ctx.setTaskState({ found, missed, phase, missIdx });
             const nextWord = missed[missIdx];
             const hintObj = question.words.find(w => w.word === nextWord);
             
             if (hintObj?.hint_audio_url) {
               ctx.playAudio(hintObj.hint_audio_url, () => ctx.startListening());
             } else {
               ctx.speak(hintObj.hint, () => ctx.startListening());
             }
           } else {
             // НОВОЕ: Передаем массив объектов
             ctx.next(found, found.filter(f => f.status === 'free').length);
           }
         }
       } else if (phase === 'free') {
         question.words.forEach(wObj => { 
           if (fullText.includes(wObj.word) && !isFound(wObj.word)) {
             found.push({ word: wObj.word, status: 'free' });
           }
         });
         ctx.setTaskState({ found, missed, phase, missIdx });
         if (found.length === question.words.length) {
            // НОВОЕ: Передаем массив объектов
            ctx.next(found, found.length);
         }
       }
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      let found = state.found || [];
      let missed = state.missed || [];
      let phase = state.phase || 'free';
      let missIdx = state.missIdx || 0;
      
      const isFound = (w) => found.some(f => f.word === w);

      if (phase === 'free') {
         missed = question.words.filter(wObj => !isFound(wObj.word)).map(wObj => wObj.word);
         if (missed.length === 0) {
            ctx.next(found, found.filter(f => f.status === 'free').length);
            return;
         }
         missIdx = 0;
         phase = 'hint';
         ctx.setTaskState({ found, missed, phase, missIdx });
         const firstMissed = question.words.find(w => w.word === missed[0]);
         const hintAudio = firstMissed.hint_audio_url;
         if (hintAudio) {
           ctx.playAudio(hintAudio, () => ctx.startListening());
         } else {
           ctx.speak(firstMissed.hint, () => ctx.startListening());
         }
      } else if (phase === 'hint') {
         phase = 'choices';
         ctx.setTaskState({ found, missed, phase, missIdx });
         ctx.stopAll();
      } else if (phase === 'choices') {
         if (missIdx < missed.length - 1) {
           missIdx++;
           phase = 'hint';
           ctx.setTaskState({ found, missed, phase, missIdx });
           const nextWord = missed[missIdx];
           const hintObj = question.words.find(w => w.word === nextWord);
           const nextHintAudio = hintObj?.hint_audio_url || hintObj?.audioUrl || hintObj?.audio_url;

           if (nextHintAudio) {
             ctx.playAudio(nextHintAudio, () => ctx.startListening());
           } else if (hintObj?.hint) {
             ctx.speak(hintObj.hint, () => ctx.startListening());
           } else {
             ctx.startListening();
           }
         } else {
           // НОВОЕ: Передаем массив объектов
           ctx.next(found, found.filter(f => f.status === 'free').length);
         }
      }
    }
  },

  naming: {
    View: Naming,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const isCorrect = question.validAnswers.some(ans => finalText.includes(ans));
      ctx.next(finalText, isCorrect ? 1 : 0);
    },
    onSilenceTimeout: (question, ctx) => ctx.next('', 0)
  },
  reading: {
    View: Reading,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let read = state.read || [];
      
      const fullText = (finalText + ' ' + interimText).toLowerCase();
      const activeIdx = read.length;
      if (activeIdx < question.mediaText.length) {
        const words = question.validAnswers[activeIdx];
        const requiredMatches = Math.min(2, words.length);
        if (words.filter(w => fullText.includes(w)).length >= requiredMatches) {
          read = [...read, activeIdx];
          ctx.setTaskState({ read, accum: '' });
          if (read.length === question.mediaText.length) {
            ctx.next('reading_done');
          } else {
            ctx.startListening();
          }
        }
      }
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      let read = state.read || [];
      if (read.length < question.mediaText.length) {
        read = [...read, read.length];
        ctx.setTaskState({ read, accum: '' });
        if (read.length === question.mediaText.length) {
          ctx.next('reading_done');
        } else {
          ctx.startListening();
        }
      }
    }
  },
    repetition: {
    View: Repetition,
    onRecognitionResult: (finalText, interimText, question, ctx) => {
      const state = ctx.getTaskState() || {};
      let idx = state.idx || 0;
      let score = state.score || 0;
      
      const fullText = (finalText + ' ' + interimText).toLowerCase();
      const answers = question.validAnswers[idx];
      const isCorrect = answers.every(ans => fullText.includes(ans));
      if (isCorrect) score++;
      
      if (idx < question.mediaText.length - 1) {
        idx++;
        ctx.setTaskState({ idx, score });
        
        // НОВОЕ: Проверяем, объект это или строка
        const mediaItem = question.mediaText[idx];
        const textToSpeak = typeof mediaItem === 'string' ? mediaItem : mediaItem.text;
        const audioUrl = typeof mediaItem === 'string' ? null : mediaItem.audioUrl;

        // Если есть аудио, играем его, иначе используем TTS
        if (audioUrl) {
          ctx.playAudio(audioUrl, () => ctx.startListening());
        } else {
          ctx.speak(textToSpeak, () => ctx.startListening());
        }
      } else {
        ctx.next('repetition_done', score);
      }
    },
    onSilenceTimeout: (question, ctx) => {
      const state = ctx.getTaskState() || {};
      let idx = state.idx || 0;
      let score = state.score || 0;
      
      if (idx < question.mediaText.length - 1) {
        idx++;
        ctx.setTaskState({ idx, score });
        
        // НОВОЕ: Проверяем, объект это или строка
        const mediaItem = question.mediaText[idx];
        const textToSpeak = typeof mediaItem === 'string' ? mediaItem : mediaItem.text;
        const audioUrl = typeof mediaItem === 'string' ? null : mediaItem.audioUrl;

        if (audioUrl) {
          ctx.playAudio(audioUrl, () => ctx.startListening());
        } else {
          ctx.speak(textToSpeak, () => ctx.startListening());
        }
      } else {
        ctx.next('repetition_timeout', score);
      }
    }
  },
};